import { supabase } from './supabase';
import { CryptoService } from './crypto';
import { Note, DecryptedNote, CreateNoteInput } from './types';
import { StorageService } from './storageService';
import { ProgressCallback } from './progressTracker';

export class NotesService {
  private static async encryptAndUploadInChunks(
    fileBlob: Blob,
    onProgress?: ProgressCallback
  ): Promise<string | undefined> {
    const arrayBuffer = await fileBlob.arrayBuffer();
    const totalSize = arrayBuffer.byteLength;

    console.log(`[NotesService] Processing file: ${totalSize} bytes`);

    if (totalSize <= StorageService.ENCRYPTION_CHUNK_SIZE) {
      console.log('[NotesService] Small file, using single chunk encryption');

      if (onProgress) onProgress(20);

      const { encrypted, iv } = await CryptoService.encryptChunk(arrayBuffer);
      const hash = await CryptoService.calculateSHA256(encrypted);

      if (onProgress) onProgress(40);

      const fileName = StorageService.generateFileName('enc');

      const uploadProgressCallback = (uploadProgress: number) => {
        if (onProgress) {
          const mappedProgress = 40 + (uploadProgress * 0.6);
          onProgress(Math.round(mappedProgress));
        }
      };

      const publicUrl = await StorageService.uploadEncryptedChunks(
        [{ encrypted, iv, hash, size: encrypted.byteLength }],
        fileName,
        uploadProgressCallback
      );

      return publicUrl;
    } else {
      console.log('[NotesService] Large file, using chunked encryption');

      const totalChunks = Math.ceil(totalSize / StorageService.ENCRYPTION_CHUNK_SIZE);
      const chunks: { encrypted: ArrayBuffer; iv: string; hash: string; size: number }[] = [];

      for (let i = 0; i < totalChunks; i++) {
        const start = i * StorageService.ENCRYPTION_CHUNK_SIZE;
        const end = Math.min(start + StorageService.ENCRYPTION_CHUNK_SIZE, totalSize);
        const chunkBuffer = arrayBuffer.slice(start, end);

        console.log(`[NotesService] Encrypting chunk ${i + 1}/${totalChunks}`);

        const { encrypted, iv } = await CryptoService.encryptChunk(chunkBuffer);
        const hash = await CryptoService.calculateSHA256(encrypted);

        chunks.push({ encrypted, iv, hash, size: encrypted.byteLength });

        if (onProgress) {
          const encryptProgress = 10 + ((i + 1) / totalChunks) * 30;
          onProgress(Math.round(encryptProgress));
        }
      }

      console.log(`[NotesService] All ${totalChunks} chunks encrypted`);

      const fileName = StorageService.generateFileName('enc');

      const uploadProgressCallback = (uploadProgress: number) => {
        if (onProgress) {
          const mappedProgress = 40 + (uploadProgress * 0.6);
          onProgress(Math.round(mappedProgress));
        }
      };

      const publicUrl = await StorageService.uploadEncryptedChunks(
        chunks,
        fileName,
        uploadProgressCallback
      );

      return publicUrl;
    }
  }

  static async uploadPhoto(photoUri: string): Promise<{ url: string; size: number } | undefined> {
    try {
      const response = await fetch(photoUri);
      const fileBlob = await response.blob();
      const originalSize = fileBlob.size;

      const publicUrl = await this.encryptAndUploadInChunks(fileBlob);

      if (!publicUrl) return undefined;

      return { url: publicUrl, size: originalSize };
    } catch (error) {
      return undefined;
    }
  }

  static async uploadVideo(videoUri: string): Promise<{ url: string; size: number } | undefined> {
    try {
      const response = await fetch(videoUri);
      const fileBlob = await response.blob();
      const originalSize = fileBlob.size;

      const publicUrl = await this.encryptAndUploadInChunks(fileBlob);

      if (!publicUrl) return undefined;

      return { url: publicUrl, size: originalSize };
    } catch (error) {
      return undefined;
    }
  }

  static async uploadFile(
    fileUri: string,
    onProgress?: ProgressCallback
  ): Promise<{ url: string; size: number } | undefined> {
    try {
      if (onProgress) onProgress(5);

      const response = await fetch(fileUri);
      const fileBlob = await response.blob();
      const originalSize = fileBlob.size;

      console.log(`[NotesService] Starting file upload, size: ${originalSize} bytes`);

      const publicUrl = await this.encryptAndUploadInChunks(fileBlob, onProgress);

      if (!publicUrl) {
        console.error('[NotesService] Upload failed - no URL returned');
        return undefined;
      }

      console.log('[NotesService] Upload successful:', publicUrl);

      if (onProgress) onProgress(100);

      return { url: publicUrl, size: originalSize };
    } catch (error) {
      console.error('[NotesService] Upload error:', error);
      return undefined;
    }
  }

  static async createNote(
    input: CreateNoteInput,
    onProgress?: ProgressCallback
  ): Promise<DecryptedNote | null> {
    try {
      if (onProgress) onProgress(5);

      const encryptedContent = CryptoService.encrypt(input.content);

      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError) {
        throw userError;
      }

      if (!user.user) {
        throw new Error('Not authenticated');
      }

      if (onProgress) onProgress(10);

      let encryptedPhotoUrl: string | undefined;
      let encryptedVideoUrl: string | undefined;
      let encryptedFileUrl: string | undefined;
      let encryptedFileName: string | undefined;
      let totalFileSize = 0;

      const hasAttachments = !!(input.photoUrl || input.videoUrl || input.fileUrl);
      const progressPerAttachment = hasAttachments ? 70 / (
        (input.photoUrl ? 1 : 0) + (input.videoUrl ? 1 : 0) + (input.fileUrl ? 1 : 0)
      ) : 0;

      let currentProgress = 10;

      if (input.photoUrl) {
        const photoProgressCallback = (progress: number) => {
          if (onProgress) {
            const mappedProgress = currentProgress + (progress / 100) * progressPerAttachment;
            onProgress(Math.round(mappedProgress));
          }
        };

        const uploadResult = await this.uploadFile(input.photoUrl, photoProgressCallback);
        if (uploadResult) {
          encryptedPhotoUrl = CryptoService.encrypt(uploadResult.url);
          totalFileSize += uploadResult.size;
        }
        currentProgress += progressPerAttachment;
      }

      if (input.videoUrl) {
        const videoProgressCallback = (progress: number) => {
          if (onProgress) {
            const mappedProgress = currentProgress + (progress / 100) * progressPerAttachment;
            onProgress(Math.round(mappedProgress));
          }
        };

        const uploadResult = await this.uploadFile(input.videoUrl, videoProgressCallback);
        if (uploadResult) {
          encryptedVideoUrl = CryptoService.encrypt(uploadResult.url);
          totalFileSize += uploadResult.size;
        }
        currentProgress += progressPerAttachment;
      }

      if (input.fileUrl) {
        const fileProgressCallback = (progress: number) => {
          if (onProgress) {
            const mappedProgress = currentProgress + (progress / 100) * progressPerAttachment;
            onProgress(Math.round(mappedProgress));
          }
        };

        const uploadResult = await this.uploadFile(input.fileUrl, fileProgressCallback);
        if (uploadResult) {
          encryptedFileUrl = CryptoService.encrypt(uploadResult.url);
          totalFileSize += uploadResult.size;
        }
        currentProgress += progressPerAttachment;

        if (input.fileName) {
          encryptedFileName = CryptoService.encrypt(input.fileName);
        }
      }

      if (onProgress) onProgress(85);

      const insertData: any = {
        user_id: user.user.id,
        encrypted_content: encryptedContent,
        category_id: input.categoryId,
        photo_url: encryptedPhotoUrl,
        video_url: encryptedVideoUrl,
        file_url: encryptedFileUrl,
        file_name: encryptedFileName,
        file_type: input.fileType,
        file_size: totalFileSize,
        latitude: input.latitude,
        longitude: input.longitude,
      };

      if (onProgress) onProgress(90);

      const { data, error } = await supabase
        .from('notes')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('[NotesService] Database insert error:', error);
        return null;
      }

      if (onProgress) onProgress(100);

      console.log('[NotesService] Note created successfully:', data.id);

      return this.decryptNote(data as Note);
    } catch (error) {
      console.error('[NotesService] Create note error:', error);
      return null;
    }
  }

  static async getNotes(limit?: number, offset?: number): Promise<DecryptedNote[]> {
    try {
      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError) {
        return [];
      }

      if (!user.user) {
        return [];
      }

      let query = supabase
        .from('notes')
        .select('*, categories(*), note_photos(*)')
        .eq('user_id', user.user.id)
        .order('created_at', { ascending: false });

      if (limit !== undefined) {
        query = query.limit(limit);
      }

      if (offset !== undefined) {
        query = query.range(offset, offset + (limit || 50) - 1);
      }

      const { data, error } = await query;

      if (error) {
        return [];
      }

      const decrypted = (data as any[]).map(note => this.decryptNote(note));
      return decrypted;
    } catch (error) {
      return [];
    }
  }

  static async getNotesCount(): Promise<number> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return 0;

      const { count, error } = await supabase
        .from('notes')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.user.id);

      if (error) {
        return 0;
      }

      return count || 0;
    } catch (error) {
      return 0;
    }
  }

  static async getNotesWithPhotosCount(): Promise<number> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return 0;

      const { count, error } = await supabase
        .from('notes')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.user.id)
        .not('photo_url', 'is', null);

      if (error) {
        return 0;
      }

      return count || 0;
    } catch (error) {
      return 0;
    }
  }

  static async getNotesWithVideosCount(): Promise<number> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return 0;

      const { count, error } = await supabase
        .from('notes')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.user.id)
        .not('video_url', 'is', null);

      if (error) {
        return 0;
      }

      return count || 0;
    } catch (error) {
      return 0;
    }
  }

  static async getTotalStorageSize(): Promise<number> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return 0;

      const { data, error } = await supabase
        .from('notes')
        .select('file_size')
        .eq('user_id', user.user.id);

      if (error) {
        return 0;
      }

      const totalSize = data?.reduce((sum: number, note: any) => {
        return sum + (note.file_size || 0);
      }, 0);

      return totalSize || 0;
    } catch (error) {
      return 0;
    }
  }

  static async deleteNote(noteId: string): Promise<boolean> {
    try {
      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError) {
        return false;
      }

      if (!user.user) {
        return false;
      }

      const { data: note, error: fetchError } = await supabase
        .from('notes')
        .select('photo_url, video_url, file_url')
        .eq('id', noteId)
        .maybeSingle();

      if (fetchError) {
        return false;
      }

      if (note) {
        if (note.photo_url) {
          try {
            const decryptedPhotoUrl = CryptoService.decrypt(note.photo_url);
            await StorageService.deleteFile(decryptedPhotoUrl);
          } catch (e) {
          }
        }

        if (note.video_url) {
          try {
            const decryptedVideoUrl = CryptoService.decrypt(note.video_url);
            await StorageService.deleteFile(decryptedVideoUrl);
          } catch (e) {
          }
        }

        if (note.file_url) {
          try {
            const decryptedFileUrl = CryptoService.decrypt(note.file_url);
            await StorageService.deleteFile(decryptedFileUrl);
          } catch (e) {
          }
        }
      }

      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', noteId);

      if (error) {
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  static async updateNote(noteId: string, content: string, categoryId?: string | null): Promise<boolean> {
    const encryptedContent = CryptoService.encrypt(content);

    const updateData: any = { encrypted_content: encryptedContent };

    if (categoryId !== undefined) {
      updateData.category_id = categoryId;
    }

    const { error } = await supabase
      .from('notes')
      .update(updateData)
      .eq('id', noteId);

    if (error) {
      return false;
    }

    return true;
  }

  static decryptNote(note: any): DecryptedNote {
    let decryptedPhotoUrl: string | undefined;
    if (note.photo_url) {
      try {
        decryptedPhotoUrl = CryptoService.decrypt(note.photo_url);
      } catch (e) {
        decryptedPhotoUrl = note.photo_url;
      }
    }

    let decryptedVideoUrl: string | undefined;
    if (note.video_url) {
      try {
        decryptedVideoUrl = CryptoService.decrypt(note.video_url);
      } catch (e) {
        decryptedVideoUrl = note.video_url;
      }
    }

    let decryptedFileUrl: string | undefined;
    if (note.file_url) {
      try {
        decryptedFileUrl = CryptoService.decrypt(note.file_url);
      } catch (e) {
        decryptedFileUrl = note.file_url;
      }
    }

    let decryptedFileName: string | undefined;
    if (note.file_name) {
      try {
        decryptedFileName = CryptoService.decrypt(note.file_name);
      } catch (e) {
        decryptedFileName = note.file_name;
      }
    }

    return {
      ...note,
      content: CryptoService.decrypt(note.encrypted_content),
      photo_url: decryptedPhotoUrl,
      video_url: decryptedVideoUrl,
      file_url: decryptedFileUrl,
      file_name: decryptedFileName,
      category: note.categories ? {
        ...note.categories,
        name: CryptoService.decrypt(note.categories.name),
      } : undefined,
      photos: note.note_photos || [],
      isProtected: !!note.password_hash,
    };
  }

  static async setNotePassword(noteId: string, password: string | null): Promise<boolean> {
    try {
      const passwordHash = password ? CryptoService.encrypt(password) : null;

      const { error } = await supabase
        .from('notes')
        .update({ password_hash: passwordHash })
        .eq('id', noteId);

      if (error) {
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  static async verifyNotePassword(noteId: string, password: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('notes')
        .select('password_hash')
        .eq('id', noteId)
        .maybeSingle();

      if (error || !data) {
        return false;
      }

      if (!data.password_hash) {
        return true;
      }

      const decryptedPassword = CryptoService.decrypt(data.password_hash);
      return decryptedPassword === password;
    } catch (error) {
      return false;
    }
  }

  static async addPhotoToNote(noteId: string, photoUrl: string): Promise<boolean> {
    const { error } = await supabase
      .from('note_photos')
      .insert({
        note_id: noteId,
        photo_url: photoUrl,
      });

    if (error) {
      return false;
    }

    return true;
  }
}
